import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { useEstimateNorms } from '../shared/hooks/useEstimateNormsApply';

export function NormsListPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [categoryCode, setCategoryCode] = useState('all');
  const { data: norms = [], isLoading } = useEstimateNorms(profile?.company_id, categoryCode);

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="shrink-0 p-4">
        <PageHeaderNew title="Thư viện định mức" subtitle="Quản lý định mức vật tư, nhân công, thiết bị để bung tài nguyên vào dự toán" className="mb-4" />
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <select value={categoryCode} onChange={(event) => setCategoryCode(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs">
            <option value="all">Tất cả nhóm</option>
            <option value="trees">Cây xanh</option>
            <option value="shrubs">Cây bụi</option>
            <option value="grass">Cỏ / phủ nền</option>
            <option value="irrigation">Tưới</option>
            <option value="lighting">Đèn</option>
            <option value="hardscape">Hardscape</option>
          </select>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate('/estimates/process-templates')}>
              <i className="ri-node-tree mr-2" />
              Quy trình mẫu
            </Button>
            <AuthButton module="estimates" action="create" onClick={() => navigate('/estimates/norms/new')}>
              <i className="ri-add-line mr-2" />
              Tạo định mức
            </AuthButton>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {isLoading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-16 rounded-md" />)}</div>
        ) : norms.length === 0 ? (
          <EmptyState title="Chưa có định mức" description="Tạo định mức đầu tiên để áp vào hạng mục hoặc bước quy trình." />
        ) : (
          <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
            <div className="overflow-auto">
              <table className="w-full min-w-[760px] border-collapse text-left">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">Mã</th>
                    <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">Tên định mức</th>
                    <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">Nhóm</th>
                    <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">ĐVT</th>
                    <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {norms.map((norm) => (
                    <tr key={norm.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/estimates/norms/${norm.id}`)}>
                      <td className="p-3 sm:text-sm text-xs text-slate-700"><Badge variant="info">{norm.norm_code}</Badge></td>
                      <td className="p-3 sm:text-sm text-xs font-medium text-slate-900">{norm.norm_name}</td>
                      <td className="p-3 sm:text-sm text-xs text-slate-600">{norm.category_code}</td>
                      <td className="p-3 sm:text-sm text-xs text-slate-600">{norm.unit}</td>
                      <td className="p-3 sm:text-sm text-xs"><Badge variant={norm.status_code === 'active' ? 'success' : 'neutral'}>{norm.status_code}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
